import type { Claims } from '@auth0/nextjs-auth0';
import type { AuthResponseDataScopesInner, GetFirmSettingsData, Permission, Resource } from '@seccl/client';

import type { IFirmBranding } from '../../helpers/branding/types';

export interface IFirmDetails {
  firmId: string;
  nodeId: string;
  windowTitle?: string;
  hosts?: string[];
}

/** The firm/node the request is for. Always derived from the host. */
export interface IHostDetails {
  firmId: string;
  nodeId: string;
}

/** Details about the logged-in user. Firm and node are determined by the host, not the user. */
export interface IUserSession {
  /** The user ID. */
  userId: string;
  /** The user claims. */
  user: Claims;
  /** The scopes associated with the authenticated session. */
  scopes: AuthResponseDataScopesInner[];
  /** The feature flags enabled for the session */
  featureFlags: string[];
}

export type UnauthenticatedSession = {
  /** Indicates the session type as unauthenticated. */
  type: 'UNAUTHENTICATED';
  /** Information for redirection. */
  redirect: {
    /** The URL destination for the redirection. */
    destination: string;
    /** Whether or not the redirection is permanent. */
    permanent: boolean;
  };
  /** The branding information for the firm, if available. */
  branding: IFirmBranding | null;
  /** The hosts associated with the user. */
  hosts?: string[];
  /** The firm/node resolved from the host, if available. */
  host?: IHostDetails;
};

export type AuthenticatedSession = {
  /** Indicates the session type as authenticated. */
  type: 'AUTHENTICATED';
  /** The API token for the authenticated session. */
  apiToken: string;
  /** The firm ID associated with the authenticated user. */
  firmId: string;
  /** The node ID associated with the authenticated user. */
  nodeId: string;
  /** The hosts associated with the authenticated user. */
  hosts?: string[];
  /** A helper function to check if the logged-in user has a given scope. */
  hasScope: (resource: Resource, permission: Permission) => boolean;
  /** A helper function to check if the logged-in user has access to a given resource. */
  hasAccess: (resource: Resource) => boolean;
  /** Checks if the user firm has the given feature flag. */
  hasFeatureFlag: (featureFlag: string) => boolean;
  /** Checks if the firm config has the given config key. Defaults to firm level rather than node level. */
  hasFirmConfig: (configKey: string, level: 'firm' | 'node') => Promise<boolean>;
  /** The branding information for the firm. */
  branding: IFirmBranding;
  /** MS Clarity details for the env */
  clarityProjectId: string | null;
  /** Session details for the authenticated user. */
  session: IUserSession;
  /** The firm settings (pfolio service details) for the firm. */
  firmSettings: GetFirmSettingsData;
  /** True to enable the migrations tab to display */
  canViewMigrations: boolean;
  /** True to enable bespoke developer tools to display */
  showDevTools: boolean;
  /** True to enable Quick Actions (every environment except production). */
  enableQuickActions: boolean;
};
/**
 * The serialisable subset of an authenticated session that is sent to the client as `pageProps`
 * (functions and the API token never leave the server).
 */
export type AuthenticatedPageProps = Pick<
  AuthenticatedSession,
  | 'session'
  | 'branding'
  | 'clarityProjectId'
  | 'showDevTools'
  | 'enableQuickActions'
  | 'canViewMigrations'
  | 'firmSettings'
> & { host: IHostDetails };

/** The serialisable subset of an unauthenticated session that is sent to the client as `pageProps`. */
export type UnauthenticatedPageProps = Partial<Pick<UnauthenticatedSession, 'branding' | 'host'>>;

/** The props `_app` receives from a page's `getServerSideProps`, in addition to any page specific props. */
export type AppPageProps = AuthenticatedPageProps | UnauthenticatedPageProps;

export const isAuthenticatedPageProps = (props: AppPageProps): props is AuthenticatedPageProps => 'session' in props;

/**
 * Represents a server-side session.
 * The session can either be AUTHENTICATED or UNAUTHENTICATED.
 */
export type ProtectedSession = UnauthenticatedSession | AuthenticatedSession;
